import { createReducer, on } from '@ngrx/store';
import {
  UserSubscription,
  SubscriptionPlansResponse,
  SecondsProductsResponse
} from '@app/core/api/stripe';
import { PaymentProduct } from '@app/core/infrastructure/payment';
import { StripeActions } from './stripe.actions';

export interface StripeState {
  // Subscription Plans (cached)
  subscriptionPlans: SubscriptionPlansResponse | null;
  subscriptionPlansLoading: boolean;
  subscriptionPlansError: string | null;

  // Seconds Products (cached)
  secondsProducts: SecondsProductsResponse | null;
  secondsProductsLoading: boolean;
  secondsProductsError: string | null;

  // User Subscription
  userSubscription: UserSubscription | null;
  userSubscriptionLoading: boolean;
  userSubscriptionError: string | null;

  // Realtime subscription (includes session usage calculations)
  realtimeSubscription: UserSubscription | null;

  // Session tracking
  isSessionActive: boolean;
  sessionUsageSeconds: number;
  initialRemainingSeconds: number | null;
  sessionMultiplier: number;

  // Auto-refresh state
  isAutoRefreshActive: boolean;

  // Authentication state
  isAuthenticated: boolean;

  // Checkout/Portal loading states
  checkoutLoading: boolean;
  checkoutError: string | null;  // Track checkout errors
  portalLoading: boolean;
  portalError: string | null;  // Track portal errors
  navigationError: string | null; // Track external navigation errors

  // Session check
  sessionCheckLoading: boolean;
  sessionCheckResponse: any | null;

  // Activate subscription (end trial early)
  activateSubscriptionLoading: boolean;
  activateSubscriptionError: string | null;

  // IAP (In-App Purchase) state
  iapProducts: PaymentProduct[];
  iapProductsLoading: boolean;
  iapProductsError: string | null;
  iapPurchaseLoading: boolean;
  iapPurchaseError: string | null;
  iapRestoreLoading: boolean;
  iapRestoreError: string | null;
}

export const initialState: StripeState = {
  subscriptionPlans: null,
  subscriptionPlansLoading: false,
  subscriptionPlansError: null,

  secondsProducts: null,
  secondsProductsLoading: false,
  secondsProductsError: null,

  userSubscription: null,
  userSubscriptionLoading: false,
  userSubscriptionError: null,

  realtimeSubscription: null,

  isSessionActive: false,
  sessionUsageSeconds: 0,
  initialRemainingSeconds: null,
  sessionMultiplier: 1,

  isAutoRefreshActive: false,

  isAuthenticated: false,

  checkoutLoading: false,
  checkoutError: null,
  portalLoading: false,
  portalError: null,
  navigationError: null,

  sessionCheckLoading: false,
  sessionCheckResponse: null,

  activateSubscriptionLoading: false,
  activateSubscriptionError: null,

  iapProducts: [],
  iapProductsLoading: false,
  iapProductsError: null,
  iapPurchaseLoading: false,
  iapPurchaseError: null,
  iapRestoreLoading: false,
  iapRestoreError: null,
};

export const stripeReducer = createReducer(
  initialState,

  // Authentication State
  on(StripeActions.setAuthenticationState, (state, { isAuthenticated }) => ({
    ...state,
    isAuthenticated,
    // Clear sensitive data when logging out
    ...(isAuthenticated ? {} : {
      userSubscription: null,
      realtimeSubscription: null,
      isSessionActive: false,
      sessionUsageSeconds: 0,
      initialRemainingSeconds: null,
      isAutoRefreshActive: false,
    })
  })),

  // All Plans (combined endpoint - replaces legacy separate endpoints)
  on(StripeActions.loadAllPlans, (state) => ({
    ...state,
    subscriptionPlansLoading: true,
    subscriptionPlansError: null,
    secondsProductsLoading: true,
    secondsProductsError: null
  })),
  on(StripeActions.loadAllPlansSuccess, (state, { response }) => ({
    ...state,
    subscriptionPlans: { plans: response.subscription_plans },
    subscriptionPlansLoading: false,
    secondsProducts: { products: response.seconds_products },
    secondsProductsLoading: false
  })),
  on(StripeActions.loadAllPlansFailure, (state, { error }) => ({
    ...state,
    subscriptionPlansLoading: false,
    subscriptionPlansError: error,
    secondsProductsLoading: false,
    secondsProductsError: error
  })),

  // User Subscription
  on(StripeActions.loadUserSubscription, (state) => ({
    ...state,
    userSubscriptionLoading: true,
    userSubscriptionError: null
  })),
  on(StripeActions.loadUserSubscriptionSuccess, (state, { subscription }) => ({
    ...state,
    userSubscription: subscription,
    realtimeSubscription: subscription, // Initialize realtime with base subscription
    userSubscriptionLoading: false
  })),
  on(StripeActions.loadUserSubscriptionFailure, (state, { error }) => ({
    ...state,
    userSubscriptionLoading: false,
    userSubscriptionError: error
  })),

  // Auto-refresh
  on(StripeActions.startAutoRefresh, (state) => ({
    ...state,
    isAutoRefreshActive: true
  })),
  on(StripeActions.stopAutoRefresh, (state) => ({
    ...state,
    isAutoRefreshActive: false
  })),

  // Session Tracking
  on(StripeActions.startSessionTracking, (state, { initialRemainingSeconds, initialMultiplier }) => ({
    ...state,
    isSessionActive: true,
    sessionUsageSeconds: 0,
    initialRemainingSeconds: initialRemainingSeconds ?? null,
    sessionMultiplier: initialMultiplier ?? 1
  })),
  on(StripeActions.stopSessionTracking, (state) => ({
    ...state,
    isSessionActive: false,
    sessionUsageSeconds: 0,
    initialRemainingSeconds: null,
    sessionMultiplier: 1
  })),
  on(StripeActions.updateSessionUsage, (state, { seconds }) => ({
    ...state,
    sessionUsageSeconds: seconds
  })),
  on(StripeActions.setSessionTrackerMultiplier, (state, { multiplier }) => ({
    ...state,
    sessionMultiplier: multiplier
  })),

  // Realtime subscription update
  on(StripeActions.updateRealtimeSubscription, (state) => {
    if (!state.userSubscription) return state;

    // This will be calculated in the selector based on session usage
    return state;
  }),

  // Checkout
  on(StripeActions.createSubscriptionCheckout, (state) => ({
    ...state,
    checkoutLoading: true,
    checkoutError: null  // Clear previous errors
  })),
  on(StripeActions.createSubscriptionCheckoutSuccess, (state) => ({
    ...state,
    // Keep checkoutLoading: true - will be cleared by externalNavigationComplete after delay
    checkoutError: null
  })),
  on(StripeActions.createSubscriptionCheckoutFailure, (state, { error }) => ({
    ...state,
    checkoutLoading: false,
    checkoutError: error
  })),
  on(StripeActions.createSecondsCheckout, (state) => ({
    ...state,
    checkoutLoading: true,
    checkoutError: null  // Clear previous errors
  })),
  on(StripeActions.createSecondsCheckoutSuccess, (state) => ({
    ...state,
    // Keep checkoutLoading: true - will be cleared by externalNavigationComplete after delay
    checkoutError: null
  })),
  on(StripeActions.createSecondsCheckoutFailure, (state, { error }) => ({
    ...state,
    checkoutLoading: false,
    checkoutError: error
  })),
  
  // Checkout Redirect Error (separate from API error)
  // @deprecated Use externalNavigationFailed instead
  on(StripeActions.checkoutRedirectFailed, (state, { error }) => ({
    ...state,
    checkoutLoading: false,
    checkoutError: error
  })),

  // External Navigation Error (unified for all redirect types)
  on(StripeActions.externalNavigationFailed, (state, { context, error }) => ({
    ...state,
    checkoutLoading: context === 'checkout' ? false : state.checkoutLoading,
    portalLoading: context === 'portal' ? false : state.portalLoading,
    checkoutError: context === 'checkout' ? error : state.checkoutError,
    portalError: context === 'portal' ? error : state.portalError,
    navigationError: error
  })),

  // External Navigation Complete (after delay)
  on(StripeActions.externalNavigationComplete, (state, { context }) => ({
    ...state,
    checkoutLoading: context === 'checkout' ? false : state.checkoutLoading,
    portalLoading: context === 'portal' ? false : state.portalLoading
  })),
  
  // Clear External Navigation Loading States (browser dismissal)
  on(StripeActions.clearExternalNavigationLoadingStates, (state) => ({
    ...state,
    checkoutLoading: false,
    portalLoading: false,
    navigationError: null
  })),

  // Portal
  on(StripeActions.getCustomerPortal, (state) => ({
    ...state,
    portalLoading: true
  })),
  on(StripeActions.getCustomerPortalSuccess, (state) => ({
    ...state,
    // Keep portalLoading: true - will be cleared by externalNavigationComplete after delay
    portalError: null
  })),
  on(StripeActions.getCustomerPortalFailure, (state) => ({
    ...state,
    portalLoading: false
  })),

  // Session Check
  on(StripeActions.checkSessionAvailability, (state) => ({
    ...state,
    sessionCheckLoading: true
  })),
  on(StripeActions.checkSessionAvailabilitySuccess, (state, { response }) => ({
    ...state,
    sessionCheckLoading: false,
    sessionCheckResponse: response
  })),
  on(StripeActions.checkSessionAvailabilityFailure, (state, { error }) => ({
    ...state,
    sessionCheckLoading: false,
    sessionCheckResponse: { can_start_session: false, reason: error }
  })),

  // Clear Cache
  on(StripeActions.clearCache, (state) => ({
    ...initialState,
    isAuthenticated: state.isAuthenticated // Preserve auth state
  })),

  // Activate Subscription (End Trial Early)
  on(StripeActions.activateSubscription, (state) => ({
    ...state,
    activateSubscriptionLoading: true,
    activateSubscriptionError: null
  })),
  on(StripeActions.activateSubscriptionSuccess, (state) => ({
    ...state,
    activateSubscriptionLoading: false,
    activateSubscriptionError: null
  })),
  on(StripeActions.activateSubscriptionFailure, (state, { error }) => ({
    ...state,
    activateSubscriptionLoading: false,
    activateSubscriptionError: error
  })),

  // ==================== IAP (In-App Purchase) ====================

  // IAP Load Products
  on(StripeActions.iapLoadProducts, (state) => ({
    ...state,
    iapProductsLoading: true,
    iapProductsError: null
  })),
  on(StripeActions.iapLoadProductsSuccess, (state, { products }) => ({
    ...state,
    iapProducts: products,
    iapProductsLoading: false
  })),
  on(StripeActions.iapLoadProductsFailure, (state, { error }) => ({
    ...state,
    iapProductsLoading: false,
    iapProductsError: error
  })),

  // IAP Purchase
  on(StripeActions.iapPurchase, (state) => ({
    ...state,
    iapPurchaseLoading: true,
    iapPurchaseError: null
  })),
  on(StripeActions.iapPurchaseSuccess, (state) => ({
    ...state,
    iapPurchaseLoading: false,
    iapPurchaseError: null
  })),
  on(StripeActions.iapPurchaseFailure, (state, { error }) => ({
    ...state,
    iapPurchaseLoading: false,
    iapPurchaseError: error
  })),

  // IAP Restore
  on(StripeActions.iapRestore, (state) => ({
    ...state,
    iapRestoreLoading: true,
    iapRestoreError: null
  })),
  on(StripeActions.iapRestoreSuccess, (state) => ({
    ...state,
    iapRestoreLoading: false,
    iapRestoreError: null
  })),
  on(StripeActions.iapRestoreFailure, (state, { error }) => ({
    ...state,
    iapRestoreLoading: false,
    iapRestoreError: error
  }))
);
